import React, { useEffect } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { MultipurposePaywall, utils } from '@yola/webapp-plugins';
import {
  selectors,
  verifiers,
  constants as subscriptionConstants,
} from '@yola/subscription-manager-js';
import { i18next, integration, dialogs as editorDialogs } from '@yola/ws-sdk';
import getSiteId from 'yola-editor/src/js/modules/auth/selectors/site-id';
import getIsB2C from 'src/js/modules/config/selectors/is-b2c';
import getPartnerData from 'yola-editor/src/js/modules/user/selectors/partner-data';
import getMoneyBackGuarantee from 'yola-editor/src/js/modules/user/selectors/money-back-guarantee';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import user from 'yola-editor/src/js/modules/user';
import useUpsellSettings from 'yola-editor/src/js/modules/upsell/hooks/use-upsell-settings';
import publishing from 'yola-editor/src/js/modules/publishing';
import beforeUnloadListener from 'yola-editor/src/js/utils/before-unload-listener';
import dialogs from 'yola-editor/src/js/modules/dialogs';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import segment from 'src/js/modules/analytics/segment';
import getTemplateBuildSlugTrait from 'src/js/modules/analytics/segment/helpers/get-template-build-slug-trait';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import usePaymentMethods from '../hooks/use-payment-methods';
import useDomainsExtraPrice, { extraPrices } from '../hooks/use-domains-extra-price';
import getHighlightedHostingPackageFeatures from '../helpers/get-highlighted-hosting-package-features';
import isDifmOfferBannerVisible from '../helpers/is-difm-offer-banner-visible';
import isLimitedOfferAvailable from '../helpers/is-limited-offer-available';
import getPurchaseFlowType from '../helpers/get-purchase-flow-type';
import getMinRequiredHostingPlan from '../helpers/get-min-required-hosting-plan';
import getActivePromotions from '../helpers/get-active-promotions';
import getCaptionsForPaywall from '../helpers/get-captions-for-paywall';
import { MAP_TERM_TO_READABLE_NAME } from '../constants/product-term';
import actionIds from '../constants/domain-purchase-action-ids.js';

const {
  constants,
  track,
  trackAsync,
  trackers: { trackEvent },
} = segment;
const {
  SubscriptionType: { B2C_HOSTING_FREE },
} = subscriptionConstants;
const { isFreeHostingPackageType, hasPaidHostingPackageType } = verifiers;

function PaywallContainer({
  upsellType,
  actionId,
  domainName = '',
  isReactivatingSubscription = false,
  isConnectThirdPartyDomain = false,
  isAiLocalizationEnabled = false,
  successUrl = '',
  forcedUpsell = '',
  onBackButtonClick = null,
  onClose = null,
  ...props
}) {
  const siteId = useSelector(getSiteId);
  const subscriptionsList = useSelector(selectors.getSubscriptionList, shallowEqual);
  const subscriptionsTypes = subscriptionsList.map(({ type }) => type);
  const isB2C = useSelector(getIsB2C);
  const hasEverHadPaidHostingSubscription = hasPaidHostingPackageType(subscriptionsTypes);
  const availablePackages = useSelector(selectors.getHostingPackages, shallowEqual);
  const partnerData = useSelector(getPartnerData, shallowEqual);
  const paymentMethods = usePaymentMethods();
  const moneyBackGuarantee = useSelector(getMoneyBackGuarantee);
  const activeHostingSubscription = useSelector(selectors.getActiveHostingSubscription);
  const currentPlan = useSelector(selectors.getCurrentPackageType);
  const purchaseFlowType = useSelector(getPurchaseFlowType);
  const { currency } = useSelector(user.selectors.getUserPreferences);
  const { id: userId } = useSelector(user.selectors.getUserData);
  const limits = useSelector(integration.selectors.getLimits);

  const dispatch = useDispatch();

  const [featureFlags] = useFeatureFlags([
    'ai_site_wizard',
    'ai_page_wizard',
    'ai_block_generator',
    'ai_text_assistant',
    'ai_text_assistant_rewrite',
    'ai_text_assistant_rewrite_v2',
    'ai_text_assistant_write',
    'site_export',
    'freedomain',
    'composite_group',
    'ai_localization',
    'nn_difm_promo',
    'free_trial_upsell',
    'gold_reinforcement',
  ]);
  const domainDiscount = useDomainsExtraPrice();
  const upsells = useUpsellSettings({
    isFreeDomainAvailable: Boolean(domainDiscount),
  });

  const {
    nn_difm_promo: isNnDifmPromoFeatureFlagEnabled,
    free_trial_upsell: isFreeTrialUpsellFeatureFlagEnabled,
    intro_price_discounts: isIntroPriceDiscountsFeatureFlagEnabled,
  } = featureFlags;

  const isCustomDomain = actionId === actionIds.BUY_DOMAIN;
  const isFreeYolaSubdomain = actionId === actionIds.GET_FREE_DOMAIN;

  const upsellName = Object.keys(upsells).find((key) => upsells[key].premiumFeature === upsellType);

  const canOfferFreeTrial =
    isFreeTrialUpsellFeatureFlagEnabled && !hasEverHadPaidHostingSubscription;
  const isUpsellSupportsFreeTrial = !upsells[upsellName].excludesFreeTrial;
  const isFreeTrialAvailable = canOfferFreeTrial && isUpsellSupportsFreeTrial;
  const isActiveTrialSubscription =
    activeHostingSubscription && verifiers.isTrialSubscription(activeHostingSubscription);
  const isTrialDomainDiscountAvailable =
    domainDiscount === extraPrices.ANNUAL_TRIAL_PACKAGE ||
    (isActiveTrialSubscription && domainDiscount === extraPrices.NO_DISCOUNTS);
  const isIntroDiscountAvailable =
    isIntroPriceDiscountsFeatureFlagEnabled && !hasEverHadPaidHostingSubscription;

  const disabledPlans =
    (isFreeTrialAvailable && !isFreeYolaSubdomain) || isIntroDiscountAvailable
      ? [B2C_HOSTING_FREE]
      : [];

  const isLimitedDifmOfferAvailable = isNnDifmPromoFeatureFlagEnabled && isLimitedOfferAvailable();
  const isDifmBannerShown = isLimitedDifmOfferAvailable && isDifmOfferBannerVisible(upsellType);

  const minRequiredPlan = getMinRequiredHostingPlan(
    {
      upsellType,
      ...(limits[upsellName] || {}),
    },
    availablePackages
  );

  const highlightedFeatures = getHighlightedHostingPackageFeatures(upsellType, !!domainDiscount, {
    isReactivatingSubscription,
    isAiLocalizationEnabled,
    isTrialDomainDiscountAvailable,
  });

  const activePromotions = getActivePromotions({
    upsellType,
    isFreeTrialAvailable,
    isFreeTrialUpsellFeatureFlagEnabled,
    isNnDifmPromoFeatureFlagEnabled,
  });

  const captions = getCaptionsForPaywall({
    upsellType,
    upsells,
    details: {
      isCustomDomain,
      isReactivatingSubscription,
      isConnectThirdPartyDomain,
      isFreeTrialAvailable,
      isFreeTrialUpsellFeatureFlagEnabled,
      forcedUpsell,
    },
  });

  const trackingData = {
    siteId,
    templateBuildSlug: getTemplateBuildSlugTrait(),
    premiumFeature: upsellType,
    purchaseFlowType,
  };

  const onSelect = (paymentUrl, selectedPlan, selectedTerm, paymentParams) => {
    const billingUrl = isFreeTrialAvailable
      ? utils.generatePaymentUrl({ ...paymentParams, trial_package: '1' })
      : paymentUrl;
    beforeUnloadListener.remove();

    if (isFreeYolaSubdomain && isFreeHostingPackageType(selectedPlan)) {
      track(constants.events.PLAN_SELECTOR_PLAN_SELECT_BUTTON_CLICKED, {
        ...trackingData,
        productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
        selectedTerm,
        selectedPlan,
      });

      editorDialogs.operations.hide();
      dispatch(publishing.actions.publishWebsite(domainName));
      return;
    }

    dispatch(
      dialogs.actions.show(dialogs.dialogTypes.PENDING, {
        captions: {
          title: i18next.t('Loading...'),
        },
      })
    );

    const trackUpgradeInitiated = trackAsync(constants.events.UPGRADE_INITIATED, {
      ...trackingData,
      productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
      trial: isFreeTrialAvailable,
    });

    const trackPlanSelected = trackAsync(
      constants.events.PLAN_SELECTOR_PLAN_SELECT_BUTTON_CLICKED,
      {
        ...trackingData,
        productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
        selectedTerm,
        selectedPlan,
      }
    );

    Promise.all([trackUpgradeInitiated, trackPlanSelected]).then(() => {
      saveSiteWithEffect(() => {
        beforeUnloadListener.remove();
        redirectToUrl(billingUrl);
      });
    });
  };

  const onTermChange = (term) => {
    track(constants.events.PLAN_SELECTOR_TERM_TOGGLE_CLICKED, {
      ...trackingData,
      productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
      selectedTerm: MAP_TERM_TO_READABLE_NAME[term],
    });
  };

  const onTooltipHover = (id) => {
    track(constants.events.PLAN_SELECTOR_TOOLTIP_DISPLAYED, {
      ...trackingData,
      productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
      featureTooltip: id,
    });
  };

  const onPeriodChange = (_, paymentPeriod) => {
    track(constants.events.PLAN_SELECTOR_TERM_TOGGLE_CLICKED, {
      ...trackingData,
      productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
      selectedTerm: paymentPeriod,
    });
  };

  const onScroll = (event) => {
    const {
      target: { scrollTop, scrollHeight, offsetHeight },
    } = event;

    if (scrollTop === scrollHeight - offsetHeight) {
      track(constants.events.PLAN_SELECTOR_SCROLLED_TO_BOTTOM, {
        ...trackingData,
        productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
      });
    }
  };

  useEffect(() => {
    trackEvent(constants.events.PLAN_SELECTOR_DISPLAYED, {
      premiumFeature: upsellType,
      purchaseFlowType,
      productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
      activePromotions,
      actionId,
      trial: !!isFreeTrialAvailable,
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <MultipurposePaywall
      {...props}
      userId={userId}
      currentPlan={currentPlan}
      currentTerm={activeHostingSubscription?.term}
      currency={currency}
      minRequiredPlan={minRequiredPlan}
      availablePlans={availablePackages}
      disabledPlans={props.disabledPlans || disabledPlans}
      successUrl={successUrl}
      activePromotions={activePromotions}
      isFreeTrialAvailable={isFreeTrialAvailable}
      captions={captions}
      siteId={siteId}
      domainDiscount={domainDiscount}
      isB2C={isB2C}
      partnerId={partnerData.id}
      partnerName={partnerData.name}
      paymentMethods={paymentMethods}
      moneyBackGuarantee={moneyBackGuarantee}
      featureFlags={featureFlags}
      isTrialDomainDiscountAvailable={isTrialDomainDiscountAvailable}
      highlightedFeatures={highlightedFeatures}
      isCtaVisible={!isReactivatingSubscription}
      isLimitedOfferAvailable={isLimitedDifmOfferAvailable}
      isDifmOfferBannerVisible={isDifmBannerShown}
      isIntroDiscountAvailable={isIntroDiscountAvailable}
      {...(isCustomDomain && { domainName })}
      onSelect={onSelect}
      onPeriodChange={onPeriodChange}
      onTooltipHover={onTooltipHover}
      onTermChange={onTermChange}
      onScroll={onScroll}
      onBackButtonClick={onBackButtonClick}
      onClose={onClose}
    />
  );
}

PaywallContainer.propTypes = {
  upsellType: PropTypes.string.isRequired,
  actionId: PropTypes.string.isRequired,
  isReactivatingSubscription: PropTypes.bool,
  isAiLocalizationEnabled: PropTypes.bool,
  isConnectThirdPartyDomain: PropTypes.bool,
  successUrl: PropTypes.string,
  domainName: PropTypes.string,
  forcedUpsell: PropTypes.string,
  disabledPlans: PropTypes.arrayOf(PropTypes.string),
  onBackButtonClick: PropTypes.func,
  onClose: PropTypes.func,
};

export default PaywallContainer;
